import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Button, Alert, Typography, Result } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';

const { Title, Paragraph } = Typography;

// Mirrors the auth-service password policy (ResetPasswordDto): >= 8 chars, with
// upper + lower case and a number or special character.
const PASSWORD_RULES = [
  { required: true, message: 'Enter a new password' },
  { min: 8, message: 'At least 8 characters' },
  {
    pattern: /((?=.*\d)|(?=.*\W+))(?![.\n])(?=.*[A-Z])(?=.*[a-z]).*$/,
    message: 'Use upper + lower case and a number or symbol',
  },
];

/** Public page: complete a password reset using the ?token= from the email link. */
const ResetPasswordPage: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFinish = async (values: { newPassword: string }) => {
    setLoading(true);
    setError(null);
    try {
      await authService.resetPassword(token, values.newPassword);
      setDone(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'This reset link is invalid or has expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Card className="shadow-lg" style={{ maxWidth: 420, margin: '0 auto' }}>
        <Result
          status="error"
          title="Invalid reset link"
          subTitle="This link is missing its token. Request a new one."
          extra={
            <Link to="/forgot-password">
              <Button type="primary">Request a new link</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  if (done) {
    return (
      <Card className="shadow-lg" style={{ maxWidth: 420, margin: '0 auto' }}>
        <Result
          status="success"
          title="Password updated"
          subTitle="You can now sign in with your new password."
          extra={
            <Button type="primary" onClick={() => navigate('/login')}>
              Sign in
            </Button>
          }
        />
      </Card>
    );
  }

  return (
    <Card className="shadow-lg" style={{ maxWidth: 420, margin: '0 auto' }}>
      <div className="text-center mb-6">
        <Title level={3} style={{ marginBottom: 4 }}>
          Choose a new password
        </Title>
        <Paragraph type="secondary">Enter and confirm your new password.</Paragraph>
      </div>

      {error && <Alert type="error" showIcon className="mb-4" message={error} style={{ marginBottom: 16 }} />}

      <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item name="newPassword" label="New password" rules={PASSWORD_RULES} hasFeedback>
          <Input.Password prefix={<LockOutlined />} placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          name="confirm"
          label="Confirm new password"
          dependencies={['newPassword']}
          hasFeedback
          rules={[
            { required: true, message: 'Confirm your password' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                return !value || getFieldValue('newPassword') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Passwords do not match'));
              },
            }),
          ]}
        >
          <Input.Password prefix={<LockOutlined />} placeholder="••••••••" autoComplete="new-password" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 8 }}>
          <Button type="primary" htmlType="submit" block loading={loading}>
            Update password
          </Button>
        </Form.Item>
      </Form>

      <div className="text-center mt-2">
        <Link to="/login">Back to sign in</Link>
      </div>
    </Card>
  );
};

export default ResetPasswordPage;
